<template>
    <div v-show="!errorInResponse && isLoaded" class="tab-pane active">
        <div v-show="!isListEmpty" class="profiles-with-navigation no-print">
            <div class="common-nav-wrap">
                <Simplebar class="with-border-track">
                    <div class="common-nav">
                        <Sidebar
                            :parent-name="ROUTE_NAMES.WIRELESS_MODULES"
                            :list="mappedWirelessModules"
                            :current-id="currentWirelessModuleId"
                            :search-placeholder="$t('wirelessModules.searchPlaceholder')"
                            :add-new-text="$t('wirelessModules.addNewWirelessModule')"
                            :icon-src="'static/img/devices/'"
                            @delete="deleteById"
                        />
                    </div>
                </Simplebar>
            </div>
            <div v-if="isLoaded" class="component-with-nav-body">
                <WirelessModulesForm
                    v-model="wirelessDevice"
                    :existing-wireless-modules="existingWirelessModules"
                    :wireless-device-types="wirelessDeviceTypes"
                    :wireless-device-types-options="wirelessDeviceTypesOptions"
                    :location-options="locationOptions"
                    :access-group-options="accessGroupOptions"
                    :wireless-device-buddy-options="wirelessDeviceBuddyOptions"
                    :current-auto-request="cancelTokenWirelessAuto"
                    :form-mode="formMode"
                    :no-buddy="noBuddy(wirelessDevice.device_type)"
                    :all-wireless-modules="allWirelessModules"
                    :to-decimal="toDecimal"
                    @updateMode="updateFormMode"
                />
            </div>
        </div>
        <div v-if="isListEmpty" class="data-empty">
            <h3>{{ $t('wirelessModules.empty') }}</h3>
        </div>
        <div class="print-only">
            <h4 v-if="showPrintKeyword">{{ $t('print.search', { val: searchKeyword }) }}</h4>
            <table v-if="wirelessModules.length > 0" class="table print-wireless">
                <thead>
                    <th v-text="$t('wirelessModules.placeholder.deviceId')"></th>
                    <th v-text="$t('wirelessModules.placeholder.name')"></th>
                    <th v-text="$t('wirelessModules.placeholder.homeLocation')"></th>
                    <th v-text="$t('wirelessModules.label.useDynamicLocation')"></th>
                    <th v-text="$t('wirelessModules.label.useDynamicSpeech')"></th>
                    <th v-text="$t('wirelessModules.placeholder.template')"></th>
                    <th v-text="$t('wirelessModules.placeholder.operationMode')"></th>
                    <th v-text="$t('wirelessModules.placeholder.assignmentMode')"></th>
                    <th v-text="$t('wirelessModules.placeholder.accessGroup')"></th>
                    <th v-text="$t('wirelessModules.placeholder.buddy')"></th>
                </thead>
                <tbody>
                    <tr v-for="module in filteredWirelessModules" :key="module.wireless_id">
                        <td>
                            <img :src="deviceIcons[module.device_type]" width="24" height="24" alt="device" />
                            {{
                                module.wireless_id
                                    .toString(16)
                                    .toUpperCase()
                                    .padStart(6, '0')
                            }}<br />
                            <span>{{ $t('wirelessModules.label.presence') }}: </span
                            >{{ modulePresenceText(module.presence, module.device_type) }}
                        </td>
                        <td v-text="module.name"></td>
                        <td>
                            {{ module.location ? module.location.name : '' }}
                            <span v-if="module.dynamic_location_enabled && !!module.last_location"
                                ><br />
                                <b>{{ $t('wirelessModules.label.lastKnown') }}:</b>
                                {{ module.last_location.last_location_name }}
                            </span>
                        </td>
                        <td><i v-if="module.dynamic_location_enabled" class="icon-checkmark"></i></td>
                        <td><i v-if="module.dynamic_speech_enabled" class="icon-checkmark"></i></td>
                        <td>
                            {{ module.template }}<br />{{
                                module.version ? '(v.' + formatDeviceVersion(module.version) + ')' : ''
                            }}
                        </td>
                        <td v-text="deviceModes[module.mode]"></td>
                        <td v-text="assignmentModes[module.assignment_mode]"></td>
                        <td v-text="module.access_group ? module.access_group.access_name : ''"></td>
                        <td v-text="moduleBuddyText(module.supervised_access, module.buddy)"></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import Sidebar from './common/Sidebar';
import WirelessModulesForm from './wireless-modules/WirelessModulesForm';
import fixingEditForm from './common/fixingEditForm';
import mappingState from './common/mappingState';
import { mapState, mapMutations } from 'vuex';
import orderBy from 'lodash-es/orderBy';
import Simplebar from 'simplebar-vue';
import {
    resourceWirelessDevice,
    resourceWirelessDeviceBuddies,
    resourceWirelessDeviceTypes,
    resourceLocations,
    resourceAllLocations,
    resourceAccessGroups,
    resourceWirelessAuto,
} from '../api/resources';
import { getCancelToken } from '../api';
import { errorHandler, sortLocations, ifIsNotCanceled } from './common/helpers';
import { deleteHandler } from './common/deleteHandler';
import { SUCCESS, ERROR, ROUTE_NAMES, USER_TYPES } from '../constants';

export default {
    components: {
        Sidebar,
        Simplebar,
        WirelessModulesForm,
    },

    mixins: [fixingEditForm, mappingState],

    constants: {
        ROUTE_NAMES: ROUTE_NAMES,
    },

    data() {
        return {
            wirelessDevice: {
                icon_name: '',
                wireless_id: null,
                old_wireless_id: null,
                name: '',
                location: {
                    location_id: null,
                    name: '',
                },
                template: '',
                template_id: null,
                device_type: null,
                mode: null,
                supervised_access: false,
                dynamic_location_enabled: false,
                dynamic_speech_enabled: false,
                last_location: null,
                presence: null,
                version: '',
                assignment_mode: 0,
                access_group: {
                    wireless_access_id: null,
                    access_name: '',
                },
                buddy: {
                    name: '',
                    wireless_id: null,
                },
            },
            wirelessModules: [],
            allWirelessModules: [],
            currentWirelessModuleId: null,
            isListEmpty: true,
            deviceIcons: {
                33: 'static/img/devices/NITX_24px.png',
                35: 'static/img/devices/NITX_LF_24px.png',
                36: 'static/img/devices/NIFX_with_socket_24px.png',
                37: 'static/img/devices/NITX_Staff_24px.png',
                38: 'static/img/devices/NIFX_with_pullcord_24px.png',
                41: 'static/img/devices/NUREP_24px.png',
                42: 'static/img/devices/NUWBM3_24px.png',
                43: 'static/img/devices/NUWPC3_24px.png',
                44: 'static/img/devices/NUUTX_24px.png',
                45: 'static/img/devices/NUWIR_24px.png',
            },
            deviceModes: {
                0: this.$t('wirelessModules.deviceModeOptions.storage'),
                1: this.$t('wirelessModules.deviceModeOptions.normal'),
                2: this.$t('wirelessModules.deviceModeOptions.rfTest'),
                3: this.$t('wirelessModules.deviceModeOptions.lfTest'),
            },
            assignmentModes: [
                this.$t('wirelessModules.placeholder.homeLocation'),
                this.$t('wirelessModules.placeholder.currentLocation'),
            ],
            oldDeviceType: null,
            wirelessDeviceTypes: [],
            wirelessDeviceTypesOptions: [],
            locations: [],
            locationOptions: [],
            accessGroups: [],
            accessGroupOptions: [],
            wirelessDeviceBuddies: [],
            wirelessDeviceBuddyOptions: [],
            formMode: 'manual',
            showPrintKeyword: false,
            isLoaded: false,
            errorInResponse: false,
            cancelToken: getCancelToken(),
            cancelTokenWirelessAuto: undefined,
        };
    },

    computed: {
        ...mapState(['isFormValid', 'unsavedChanges', 'assignmentArea']),

        filteredWirelessModules() {
            return this.showPrintKeyword
                ? this.wirelessModules.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : this.wirelessModules;
        },

        /**
         * Unifies array of wireless modules for proper render in sidebar
         *
         * @returns {Array|{id, name, icon: (string|string|null|string)}[]}
         */
        mappedWirelessModules() {
            return this.wirelessModules.map(i => ({
                id: i.wireless_id,
                name: i.name,
                icon: i.icon_name,
                isUsed: i.device_type === 41,
            }));
        },
        /**
         * Get list of all modules in the system except current one
         */
        existingWirelessModules() {
            let currentId = this.wirelessDevice.wireless_id
                ? this.toDecimal(this.wirelessDevice.wireless_id)
                : null;
            return currentId
                ? this.allWirelessModules.filter(item => item.wireless_id !== currentId)
                : this.allWirelessModules;
        },
    },

    watch: {
        wirelessDevice: {
            handler(obj) {
                if (this.isLoaded) {
                    if (!obj.dynamic_location_enabled) {
                        obj.dynamic_speech_enabled = false;
                    }
                    if (this.$route.name === ROUTE_NAMES.WIRELESS_MODULES__ADD) {
                        if (this.oldDeviceType !== obj.device_type) {
                            this.setWirelessDeviceTypesOptions();
                            if (this.wirelessDeviceTypesOptions.length > 0) {
                                obj.template_id = orderBy(this.wirelessDeviceTypesOptions, 'name')[0].value;
                                obj.mode = 1;
                            }

                            this.oldDeviceType = obj.device_type;
                        }
                    }
                    this.setUnsavedChanges(true);
                }
            },
            deep: true,
        },

        $route(to) {
            this.setMode(to);
            this.updateFormMode('manual');
            this.cancelAutoRequest();
        },

        searchKeyword(val) {
            this.showPrintKeyword =
                val && this.wirelessModules.some(item => item.name.toLowerCase().includes(val));
        },

        wirelessModules: {
            handler(list) {
                let keyword = this.searchKeyword;
                this.showPrintKeyword =
                    keyword && list.some(item => item.name.toLowerCase().includes(keyword));
            },
            deep: true,
        },
    },

    created() {
        this.showLoading(true);
        // Event listener for edit form styles
        window.addEventListener('resize', this.checkHeightOnResize);
        this.updateData(() => {
            return this.updateWirelessData().then(() => {
                if (this.$route.name === ROUTE_NAMES.WIRELESS_MODULES__ADD) {
                    this.$router.push({
                        name: ROUTE_NAMES.WIRELESS_MODULES,
                    });
                }
            });
        });
        return this.updateWirelessData();
    },

    beforeDestroy() {
        this.cancelAutoRequest();
        this.cancelToken.cancel();
        window.removeEventListener('resize', this.checkHeightOnResize);
        this.setFormValid(true);
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        setEmptyWirelessDevice() {
            this.wirelessDevice = {
                icon_name: null,
                wireless_id: null,
                old_wireless_id: null,
                name: null,
                location: {
                    location_id: null,
                    name: null,
                },
                template: null,
                template_id: null,
                device_type: null,
                mode: null,
                supervised_access: false,
                dynamic_location_enabled: false,
                dynamic_speech_enabled: false,
                presence: null,
                version: null,
                assignment_mode: 0,
                access_group: {
                    wireless_access_id: null,
                    access_name: null,
                },
                buddy: {
                    name: null,
                    wireless_id: null,
                },
            };
        },

        updateWirelessData() {
            this.errorInResponse = false;
            return this.loadWirelessModules()
                .then(this.loadWirelessDeviceTypes)
                .then(this.loadWirelessDeviceBuddies)
                .then(this.loadLocations)
                .then(this.loadAccessGroups)
                .then(() => {
                    this.setMode(this.$route);
                    this.isLoaded = true;
                    this.showLoading(false);
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            errorHandler(response.status, ROUTE_NAMES.WIRELESS_MODULES);
                            return response;
                        },
                    })
                );
        },

        /**
         * Set property for visibility by route's name and redirect to first item if it needs
         * @param route
         */
        setMode(route) {
            if (route.name === ROUTE_NAMES.WIRELESS_MODULES) {
                this.transit();
            } else if (route.name === ROUTE_NAMES.WIRELESS_MODULES__ADD) {
                this.isListEmpty = false;
                this.currentWirelessModuleId = null;
                this.initAdding();
            } else {
                if (
                    this.wirelessModules.length &&
                    this.wirelessModules.some(s => s.wireless_id === Number(route.params.id))
                ) {
                    this.isListEmpty = false;
                    this.currentWirelessModuleId = Number(route.params.id);
                    this.initEditing(this.currentWirelessModuleId);
                } else {
                    this.$router.replace({
                        name: ROUTE_NAMES.WIRELESS_MODULES,
                    });
                }
            }
            // Checks height of elements for their initial state
            this.$nextTick(() => {
                this.checkHeightOnResize();
            });
        },

        transit() {
            if (this.wirelessModules.length) {
                let firstId = this.wirelessModules[0].wireless_id;
                this.$router.replace({
                    name: ROUTE_NAMES.WIRELESS_MODULES__EDIT,
                    params: { id: firstId },
                });
                this.initEditing(firstId);
            } else {
                this.configButton({
                    buttonName: 'addButton',
                    action: () => this.$router.push({ name: ROUTE_NAMES.WIRELESS_MODULES__ADD }),
                });
                this.isListEmpty = true;
            }
        },

        loadWirelessModules() {
            return resourceWirelessDevice({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    let data = response.data ? orderBy(response.data, 'name') : [];
                    this.allWirelessModules = data;
                    if (this.userType === USER_TYPES.SYS_ADMIN) {
                        this.wirelessModules = data.filter(item => item.device_type !== 40);
                    } else {
                        this.wirelessModules = data.filter(
                            item => item.assignmentarea_id === this.userAreaId && item.device_type !== 40
                        );
                    }
                    if (this.userType === USER_TYPES.USER) {
                        this.wirelessModules = this.wirelessModules.filter(item => item.device_type !== 41);
                    }
                    this.isListEmpty = !this.wirelessModules.length;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: () => {
                            this.isListEmpty = true;
                        },
                    })
                );
        },

        loadWirelessDeviceTypes() {
            return resourceWirelessDeviceTypes({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.wirelessDeviceTypes = response.data ? response.data : [];
                    this.setWirelessDeviceTypesOptions();
                })
                .catch(response => {
                    this.setWirelessDeviceTypesOptions();

                    return Promise.reject(response);
                });
        },

        loadWirelessDeviceBuddies() {
            return resourceWirelessDeviceBuddies({
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.wirelessDeviceBuddies = response.data ? response.data : [];
                    this.setWirelessDeviceBuddyOptions();
                })
                .catch(response => {
                    this.setWirelessDeviceBuddyOptions();

                    return Promise.reject(response);
                });
        },

        loadLocations() {
            return (this.userType === USER_TYPES.SYS_ADMIN
                ? resourceAllLocations()
                : resourceLocations({ id: this.userAreaId })
            )
                .then(response => {
                    this.locations = response.data ? sortLocations(response.data) : [];
                    this.setLocationOptions();
                })
                .catch(response => {
                    this.setLocationOptions();

                    return Promise.reject(response);
                });
        },

        loadAccessGroups() {
            return resourceAccessGroups()
                .then(response => {
                    this.accessGroups = response.data ? response.data : [];
                    this.setAccessGroupOptions();
                })
                .catch(response => {
                    this.setAccessGroupOptions();

                    return Promise.reject(response);
                });
        },

        setCurrentWirelessDevice(id) {
            let device = this.wirelessModules.find(item => item.wireless_id === id);
            if (device) {
                this.wirelessDevice = { ...device };
                // to avoid errors in form when buddy or accessGroup is null
                if (!this.wirelessDevice.buddy) {
                    this.wirelessDevice.buddy = {
                        name: null,
                        wireless_id: null,
                    };
                }
                if (!this.wirelessDevice.access_group) {
                    this.wirelessDevice.access_group = {
                        wireless_access_id: null,
                        access_name: null,
                    };
                }
                this.wirelessDevice.old_wireless_id = this.wirelessDevice.wireless_id;
                this.wirelessDevice.presence = this.modulePresenceText(
                    this.wirelessDevice.presence,
                    this.wirelessDevice.device_type
                );
                this.idToHex();
                this.setWirelessDeviceTypesOptions();
                this.setUnsavedChanges(false);
            } else {
                this.popup({
                    type: ERROR,
                    header: this.$t('common.error'),
                    text: this.$t('wirelessModules.notFound'),
                });
                errorHandler(404, ROUTE_NAMES.WIRELESS_MODULES);
            }
        },

        setWirelessDeviceTypesOptions() {
            this.wirelessDeviceTypesOptions = [];
            if (this.wirelessDeviceTypes.length > 0) {
                let options = this.wirelessDeviceTypes.find(
                    item => item.type_id === this.wirelessDevice.device_type
                );
                if (options) {
                    options = options.template || [];
                    this.wirelessDeviceTypesOptions = options.map(item => ({
                        value: item.definition_id,
                        name: item.name,
                    }));
                }
            }
        },

        setLocationOptions() {
            this.locationOptions = [];
            if (this.locations.length) {
                this.locationOptions = this.locations.map(item => ({
                    value: item.location_id,
                    name: item.name,
                }));
            }
        },

        setAccessGroupOptions() {
            let noAccessGroupOption = [
                {
                    value: null,
                    name: this.$t('wirelessModules.list.noAccessGroup'),
                },
            ];
            let accessGroupOptions = this.accessGroups.map(item => ({
                value: item.wireless_access_id,
                name: item.name,
            }));
            this.accessGroupOptions = noAccessGroupOption.concat(orderBy(accessGroupOptions, 'name'));
        },

        setWirelessDeviceBuddyOptions() {
            let noBuddyOption = [{ value: null, name: this.$t('wirelessModules.list.noBuddy') }];
            let buddiesFormattedOptions = this.wirelessDeviceBuddies
                .filter(item => item.wireless_id !== this.toDecimal(this.wirelessDevice.wireless_id))
                .map(item => ({
                    value: item.wireless_id,
                    name: item.name + ' (' + this.toHex(item.wireless_id) + ')',
                }));
            this.wirelessDeviceBuddyOptions = noBuddyOption.concat(orderBy(buddiesFormattedOptions, 'name'));
        },

        loadWirelessData() {
            return this.loadWirelessModules().then(this.loadWirelessDeviceBuddies());
        },

        deleteById(id) {
            let deletedWirelessModule = this.wirelessModules.find(item => item.wireless_id === id);
            let confirmPart = {
                confirmTitle: this.$t('wirelessModules.modules.removingWirelessModuleTitle'),
                confirmBody: this.$t('wirelessModules.modules.removingWirelessModule', {
                    name: deletedWirelessModule ? deletedWirelessModule.name : '',
                }),
            };
            return deleteHandler(
                resourceWirelessDevice,
                id,
                this.loadWirelessData,
                ROUTE_NAMES.WIRELESS_MODULES,
                confirmPart
            );
        },

        saveWirelessDevice() {
            let data = JSON.stringify({
                wireless_id: this.toDecimal(this.wirelessDevice.wireless_id), // 201EC6 in Hex
                old_wireless_id: this.wirelessDevice.old_wireless_id,
                device_type: this.wirelessDevice.device_type,

                // Template ID
                definition_id: this.wirelessDevice.template_id,
                name: this.wirelessDevice.name,
                location_id: this.wirelessDevice.location.location_id,
                supervised_access: this.noBuddy(this.wirelessDevice.device_type)
                    ? false
                    : this.wirelessDevice.supervised_access,
                dynamic_location_enabled: this.noBuddy(this.wirelessDevice.device_type)
                    ? false
                    : this.wirelessDevice.dynamic_location_enabled,
                dynamic_speech_enabled: this.noBuddy(this.wirelessDevice.device_type)
                    ? false
                    : this.wirelessDevice.dynamic_speech_enabled,
                mode_id: this.wirelessDevice.mode,
                assignment_mode: this.wirelessDevice.assignment_mode,
                access_id: this.noBuddy(this.wirelessDevice.device_type)
                    ? null
                    : this.wirelessDevice.access_group.wireless_access_id,
                buddy_id: this.noBuddy(this.wirelessDevice.device_type)
                    ? null
                    : this.wirelessDevice.buddy.wireless_id,
            });
            return resourceWirelessDevice({ method: 'post', data });
        },

        save() {
            this.showLoading(true);
            this.wirelessDevice.name = this.wirelessDevice.name.trim();
            return this.saveWirelessDevice()
                .then(this.loadWirelessModules)
                .then(this.loadWirelessDeviceBuddies)
                .then(() => {
                    if (
                        this.$router.currentRoute.name === ROUTE_NAMES.WIRELESS_MODULES__ADD ||
                        this.$router.currentRoute.name === ROUTE_NAMES.WIRELESS_MODULES__EDIT
                    ) {
                        this.showLoading(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                        this.setUnsavedChanges(false);
                        if (
                            this.$route.name === ROUTE_NAMES.WIRELESS_MODULES__ADD ||
                            this.toDecimal(this.wirelessDevice.wireless_id) !== this.currentWirelessModuleId
                        ) {
                            this.$router.replace({
                                name: ROUTE_NAMES.WIRELESS_MODULES__EDIT,
                                params: {
                                    id: this.toDecimal(this.wirelessDevice.wireless_id),
                                },
                            });
                        }
                    }
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            if (response.status === 404 || response.status === 409) {
                                return this.loadWirelessModules().then(() => {
                                    this.showLoading(false);
                                    this.setUnsavedChanges(false);
                                    this.$nextTick(() => {
                                        this.$router.replace({
                                            name: ROUTE_NAMES.WIRELESS_MODULES,
                                        });
                                    });
                                });
                            }
                        },
                    })
                );
        },

        formatDeviceVersion(val) {
            let version;
            if (val && val.length === 6) {
                version =
                    val[0].toString() +
                    val[1].toString() +
                    '.' +
                    val[2].toString() +
                    val[3].toString() +
                    '.' +
                    val[4].toString() +
                    val[5].toString();
            } else {
                version = val;
            }
            return version;
        },

        toHex(val) {
            let hex = val.toString(16).toUpperCase();
            if (hex.length < 6) {
                for (let currentLength = hex.length; currentLength < 6; ++currentLength) {
                    hex = '0' + hex;
                }
            }
            return hex;
        },

        idToHex() {
            if (this.wirelessDevice.wireless_id) {
                this.wirelessDevice.wireless_id = this.toHex(this.wirelessDevice.wireless_id);
            }
        },

        toDecimal(val) {
            return parseInt(val, 16);
        },

        initAdding() {
            this.setAddingButtons({
                cancel: () => {
                    this.setUnsavedChanges(false);
                    this.cancelAutoRequest();
                    this.$nextTick(() => {
                        this.$router.push({ name: ROUTE_NAMES.WIRELESS_MODULES });
                    });
                },
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid,
            });
            this.setEmptyWirelessDevice();
            this.wirelessDevice.mode = 1;
            this.setWirelessDeviceTypesOptions();
            this.setWirelessDeviceBuddyOptions();
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        initEditing(wirelessModuleId) {
            this.setEditingButtons({
                add: () => this.$router.push({ name: ROUTE_NAMES.WIRELESS_MODULES__ADD }),
                undo: () => this.undo(),
                wasChanged: () => !this.unsavedChanges,
                save: () => {
                    if (this.isFormValid) {
                        this.save();
                    }
                },
                saveDisabled: () => !this.unsavedChanges || !this.isFormValid,
            });
            this.setCurrentWirelessDevice(wirelessModuleId);
            this.setWirelessDeviceBuddyOptions();
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
                this.showLoading(false);
            });
        },

        undo() {
            this.setCurrentWirelessDevice(
                this.wirelessDevice.device_type === 41
                    ? this.wirelessDevice.old_wireless_id
                    : this.toDecimal(this.wirelessDevice.wireless_id)
            );
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        updateFormMode(val) {
            this.formMode = val;
            if (this.$route.name === ROUTE_NAMES.WIRELESS_MODULES__ADD) {
                if (val === 'auto' && typeof this.cancelTokenWirelessAuto === 'undefined') {
                    this.addAuto();
                } else if (val === 'manual') {
                    this.wirelessDevice.mode = 1;
                    this.cancelAutoRequest();
                    this.setEmptyWirelessDevice();
                }
            }
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },

        getAuto() {
            this.cancelTokenWirelessAuto = getCancelToken();
            return resourceWirelessAuto({
                cancelToken: this.cancelTokenWirelessAuto.token,
            }).then(response => {
                let jsonData = response.data;
                this.cancelTokenWirelessAuto = undefined;
                if (response.data) {
                    if (response.data === 'null' || jsonData.device_type === 40) {
                        return this.getAuto();
                    }
                    return Promise.resolve(response);
                }

                return Promise.reject(response);
            });
        },

        addAuto() {
            this.setEmptyWirelessDevice();
            this.wirelessDevice.mode = 1;
            return this.getAuto()
                .then(response => {
                    let data = response.data;
                    this.wirelessDevice.wireless_id = this.toHex(data.wireless_id);
                    this.wirelessDevice.device_type = +data.device_type;
                })
                .catch(
                    ifIsNotCanceled({
                        callbackAlways: () => {
                            this.cancelTokenWirelessAuto = undefined;
                        },
                        callback: response => {
                            errorHandler(response.status, ROUTE_NAMES.WIRELESS_MODULES);
                        },
                    })
                );
        },

        cancelAutoRequest() {
            if (this.cancelTokenWirelessAuto) {
                this.cancelTokenWirelessAuto.cancel();
            }
        },

        /**
         * Checks if selected 'device type' has 'buddy' functionality
         * @param {Number} t - type of device
         * @returns {Boolean} text for print table
         */
        noBuddy(t) {
            return [33, 41, 42, 43, 44, 45].includes(t);
        },

        /**
         * Forms text for Presence column in print version
         * @param {boolean|null} presence
         * @param {number} type
         * @returns {string} text for print table
         */
        modulePresenceText(presence, type) {
            return this.noBuddy(type)
                ? this.$t('common.na')
                : presence === false
                ? this.$t('common.no')
                : this.$t('common.yes');
        },

        /**
         * Forms text for Buddy column in print version
         * @param allStaff
         * @param buddy
         * @returns {string} text for print table
         */
        moduleBuddyText(allStaff, buddy) {
            return allStaff
                ? this.$t('wirelessModules.label.allStaffMembers')
                : buddy
                ? buddy.name + ' (' + buddy.wireless_id.toString(16).toUpperCase() + ')'
                : '';
        },

        /**
         * Get assignment area id by location id
         */
        getAAreaIdByLocationId(locationId) {
            let area = this.assignmentArea.find(area =>
                area.locations.find(location => location.id === locationId)
            );
            if (area) {
                return area.id;
            }

            return null;
        },
    },
};
</script>
